import { NavLink } from 'react-router-dom';
import {
  Layers,
  Newspaper,
  Star,
  Pill,
  Mic2,
  MapPin,
  BookOpen,
  ChevronsUpDown,
  LogOut,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BailaLogo } from '@/components/BailaLogo';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

interface NavGroup {
  title?: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { to: '/admin/colecciones', label: 'Colecciones', icon: Layers },
      { to: '/admin/blog', label: 'Blog', icon: Newspaper },
    ],
  },
  {
    title: 'Página de inicio',
    items: [
      { to: '/admin/destacados', label: 'Destacados', icon: Star },
      { to: '/admin/chips', label: 'Chips', icon: Pill },
      { to: '/admin/artistas-destacados', label: 'Artistas destacados', icon: Mic2 },
      { to: '/admin/ciudades-destacadas', label: 'Ciudades destacadas', icon: MapPin },
    ],
  },
  {
    items: [{ to: '/admin/configuracion', label: 'Configuración', icon: Settings }],
  },
];

function initials(value: string | undefined) {
  if (!value) return '·';
  const local = value.split('@')[0] ?? value;
  return local.slice(0, 2).toUpperCase();
}

export function Sidebar() {
  const { member, signOut } = useAuth();
  const email = member?.email_address ?? '';
  const name = member?.name?.trim() || undefined;

  return (
    <aside className="sticky top-0 h-screen w-60 shrink-0 bg-sidebar text-sidebar-foreground border-r border-sidebar-border flex flex-col">
      <div className="px-6 py-5">
        <NavLink
          to="/admin"
          end
          className="flex items-center text-black"
          aria-label="Ir al inicio del panel"
        >
          <BailaLogo width={120} height={23} />
        </NavLink>
      </div>
      <Separator className="bg-sidebar-border" />
      <nav className="flex-1 overflow-y-auto p-3" aria-label="Navegación principal">
        <div className="flex flex-col gap-4">
          {NAV_GROUPS.map((group, groupIndex) => (
            <div key={group.title ?? `group-${groupIndex}`}>
              {group.title && (
                <p className="px-3 pb-1.5 text-xs font-semibold uppercase tracking-wide text-sidebar-foreground/60">
                  {group.title}
                </p>
              )}
              <ul className="flex flex-col gap-1">
                {group.items.map(({ to, label, icon: Icon, end }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      end={end}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                            : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
                        )
                      }
                    >
                      <Icon className="size-4" aria-hidden />
                      <span>{label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      <div className="px-3 pb-2">
        <NavLink
          to="/admin/documentacion"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
            )
          }
        >
          <BookOpen className="size-4" aria-hidden />
          <span>Documentación</span>
        </NavLink>
      </div>

      {email && (
        <>
          <Separator className="bg-sidebar-border" />
          <div className="p-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 rounded-md p-2 text-left text-sm hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground transition-colors"
                >
                  <Avatar>
                    <AvatarFallback>{initials(email)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{name ?? email}</p>
                    {name && (
                      <p className="text-xs text-muted-foreground truncate">{email}</p>
                    )}
                  </div>
                  <ChevronsUpDown className="size-4 text-muted-foreground" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="end" className="w-60">
                <DropdownMenuLabel className="flex items-center gap-2 p-2 font-normal">
                  <Avatar>
                    <AvatarFallback>{initials(email)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{name ?? email}</p>
                    {name && (
                      <p className="text-xs text-muted-foreground truncate">{email}</p>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void signOut()}>
                  <LogOut className="size-4" aria-hidden />
                  <span>Cerrar sesión</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </>
      )}
    </aside>
  );
}
